export const M365_SEARCH_ALL_SOURCES = [
  { id: "emails", label: "Email" },
  { id: "calendar", label: "Calendar" },
  { id: "files", label: "Files" },
  { id: "teams_chats", label: "Teams chats" },
  { id: "onenote", label: "OneNote" },
] as const

export type M365SearchAllSource = (typeof M365_SEARCH_ALL_SOURCES)[number]["id"]

export type M365SearchAllConfig = {
  sources: M365SearchAllSource[]
  top_per_source: number
  source_timeout_seconds: number
}

export const DEFAULT_M365_SEARCH_ALL_CONFIG: M365SearchAllConfig = {
  sources: M365_SEARCH_ALL_SOURCES.map((source) => source.id),
  top_per_source: 10,
  source_timeout_seconds: 12,
}

const SOURCE_IDS = new Set<string>(M365_SEARCH_ALL_SOURCES.map((source) => source.id))

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function clampNumber(
  value: unknown,
  { fallback, max, min }: { fallback: number; max: number; min: number }
): number {
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value ?? ""))
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(min, Math.min(parsed, max))
}

function normalizeSources(rawSources: unknown): M365SearchAllSource[] {
  if (!Array.isArray(rawSources)) return [...DEFAULT_M365_SEARCH_ALL_CONFIG.sources]

  const normalized: M365SearchAllSource[] = []
  for (const source of rawSources) {
    if (typeof source !== "string") continue
    if (!SOURCE_IDS.has(source)) continue
    if (normalized.includes(source as M365SearchAllSource)) continue
    normalized.push(source as M365SearchAllSource)
  }

  return normalized.length ? normalized : [...DEFAULT_M365_SEARCH_ALL_CONFIG.sources]
}

export function normalizeM365SearchAllConfig(config: Record<string, unknown>): M365SearchAllConfig {
  const rawSearchAll = config.search_all
  const searchAll = isRecord(rawSearchAll) ? rawSearchAll : {}

  return {
    sources: normalizeSources(searchAll.sources),
    top_per_source: Math.round(
      clampNumber(searchAll.top_per_source, {
        fallback: DEFAULT_M365_SEARCH_ALL_CONFIG.top_per_source,
        max: 25,
        min: 1,
      })
    ),
    source_timeout_seconds: clampNumber(searchAll.source_timeout_seconds, {
      fallback: DEFAULT_M365_SEARCH_ALL_CONFIG.source_timeout_seconds,
      max: 30,
      min: 1,
    }),
  }
}

export function withM365SearchAllConfig(
  config: Record<string, unknown>,
  searchAll: M365SearchAllConfig
): Record<string, unknown> {
  return {
    ...config,
    search_all: {
      sources: searchAll.sources,
      top_per_source: searchAll.top_per_source,
      source_timeout_seconds: searchAll.source_timeout_seconds,
    },
  }
}

/**
 * Shared mailboxes an admin enables for Hello AI (stored as
 * `config.shared_mailboxes`). This is a discoverability hint for the model and an
 * app-side allowlist only — it never grants access. The backend reads a shared
 * mailbox with the signed-in user's own delegated token, so Exchange still checks
 * that the user has been given access to the mailbox on every call.
 */
export type M365SharedMailbox = {
  address: string
  description: string
}

// Mirrors the backend's SMTP-address check (msgraph/m365_search.py).
const MAILBOX_ADDRESS_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/

export function isValidMailboxAddress(address: string): boolean {
  return MAILBOX_ADDRESS_RE.test(address.trim())
}

export function normalizeM365SharedMailboxes(config: Record<string, unknown>): M365SharedMailbox[] {
  const raw = config.shared_mailboxes
  let entries: unknown[] = []
  if (Array.isArray(raw)) entries = raw
  else if (typeof raw === "string") entries = raw.split(/[\n,;]+/)

  const normalized: M365SharedMailbox[] = []
  const seen = new Set<string>()
  for (const entry of entries) {
    let address = ""
    let description = ""
    if (isRecord(entry)) {
      address = String(entry.address ?? entry.email ?? "")
      description = String(entry.description ?? "")
    } else if (typeof entry === "string") {
      address = entry
    }
    address = address.trim().toLowerCase()
    description = description.trim()
    if (!address || seen.has(address) || !isValidMailboxAddress(address)) continue
    seen.add(address)
    normalized.push({ address, description })
  }
  return normalized
}

export function withM365SharedMailboxes(
  config: Record<string, unknown>,
  mailboxes: M365SharedMailbox[]
): Record<string, unknown> {
  const { shared_mailboxes: _sharedMailboxes, ...rest } = config
  if (mailboxes.length === 0) return rest
  return {
    ...rest,
    shared_mailboxes: mailboxes.map((mailbox) => ({
      address: mailbox.address,
      description: mailbox.description,
    })),
  }
}

// Admin editor line format: `address | description` (description optional).
const SHARED_MAILBOX_LINE_SEPARATOR = "|"

function sharedMailboxLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
}

export function parseSharedMailboxLines(text: string): M365SharedMailbox[] {
  const entries = sharedMailboxLines(text).map((line) => {
    const [address, ...rest] = line.split(SHARED_MAILBOX_LINE_SEPARATOR)
    return {
      address: address.trim(),
      description: rest.join(SHARED_MAILBOX_LINE_SEPARATOR).trim(),
    }
  })
  return normalizeM365SharedMailboxes({ shared_mailboxes: entries })
}

export function formatSharedMailboxLines(mailboxes: M365SharedMailbox[]): string {
  return mailboxes
    .map((mailbox) =>
      mailbox.description ? `${mailbox.address} | ${mailbox.description}` : mailbox.address
    )
    .join("\n")
}

/** Lines the editor will ignore because the part before `|` is not an SMTP address. */
export function invalidSharedMailboxLines(text: string): string[] {
  return sharedMailboxLines(text).filter(
    (line) => !isValidMailboxAddress(line.split(SHARED_MAILBOX_LINE_SEPARATOR)[0])
  )
}
